import { useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ChevronLeft, ChevronRight, Cpu, FileText, Save, Sparkles, Upload, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { parseStandardDocument, suggestRules, suggestInstrumentItems } from "@/lib/ai/simulate";
import type { ParsedStandard } from "@/lib/ai/simulate";
import type { Standard } from "@/data";
import type { Instrumento } from "@/data";

interface MetaForm {
  codigo: string;
  titulo: string;
  tipo: string;
  version: string;
  vigencia: string;
  periodoRevision: string;
  sector: string;
  descripcion: string;
  proposito: string;
}

const EMPTY_META: MetaForm = {
  codigo: "",
  titulo: "",
  tipo: "",
  version: "",
  vigencia: "",
  periodoRevision: "",
  sector: "",
  descripcion: "",
  proposito: "",
};

interface UploadedDoc {
  nombre: string;
  tamanioKb: number;
  texto: string;
}

export default function CreateStandard() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [meta, setMeta] = useState<MetaForm>(EMPTY_META);
  const [doc, setDoc] = useState<UploadedDoc | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedStandard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const standards = useDemoStore((s) => s.standards);
  const archived = useDemoStore((s) => s.archivedStandards);
  const addStandard = useDemoStore((s) => s.addStandard);
  const setElementsForStandard = useDemoStore((s) => s.setElementsForStandard);
  const setRulesForStandard = useDemoStore((s) => s.setRulesForStandard);
  const setInstrumentsForStandard = useDemoStore((s) => s.setInstrumentsForStandard);
  const logEvent = useDemoStore((s) => s.logEvent);
  const activeRole = useDemoStore((s) => s.activeRole);

  const codigoExists = useMemo(
    () => Boolean(meta.codigo && standards[meta.codigo]),
    [standards, meta.codigo],
  );

  function setErr(msg: string) { setError(msg); return false; }

  async function handleFile(file: File) {
    setError(null);
    // Para PDFs no parseamos el binario (demo frontend-only). Para .txt/.md sí leemos el texto.
    let texto = "";
    const isText = /\.(txt|md)$/i.test(file.name) || file.type.startsWith("text/");
    if (isText) {
      try { texto = await file.text(); } catch { texto = ""; }
    }
    setDoc({ nombre: file.name, tamanioKb: Math.max(1, Math.round(file.size / 1024)), texto });
    // Lanzar análisis IA automáticamente
    runAI({ nombre: file.name, tamanioKb: Math.max(1, Math.round(file.size / 1024)), texto });
  }

  function runAI(forDoc?: UploadedDoc) {
    const d = forDoc ?? doc;
    if (!d) return;
    setParsing(true);
    setParsed(null);
    setError(null);
    setTimeout(() => {
      const result = parseStandardDocument(d.texto, d.nombre);
      setParsed(result);
      // Pre-llenar el formulario con lo que extrajo la IA
      setMeta({
        codigo: result.codigo,
        titulo: result.titulo,
        tipo: result.tipo,
        version: result.version,
        vigencia: result.vigencia,
        periodoRevision: result.periodoRevision,
        sector: result.sector,
        descripcion: result.descripcion,
        proposito: result.proposito,
      });
      setParsing(false);
    }, 2000);
  }

  function removeDoc() {
    setDoc(null);
    setParsed(null);
    setMeta(EMPTY_META);
    if (fileRef.current) fileRef.current.value = "";
  }

  function validateMeta(): boolean {
    if (!meta.codigo.trim()) return setErr("El código CONOCER es obligatorio (ej. EC1781).");
    if (codigoExists) return setErr(`Ya existe un estándar con código ${meta.codigo}.`);
    if (!meta.titulo.trim()) return setErr("El nombre del estándar es obligatorio.");
    if (!meta.sector.trim()) return setErr("Indica el sector productivo.");
    setError(null);
    return true;
  }

  function save() {
    if (!validateMeta()) { setStep(2); return; }
    const p = parsed;
    const elementos = p?.elementos ?? [];
    const totalCriterios = elementos.reduce((acc, e) => acc + e.criterios.length, 0);
    const standard: Standard = {
      codigo: meta.codigo,
      titulo: meta.titulo,
      sector: meta.sector,
      comite: parsed?.comite,
      vigencia: meta.vigencia,
      estado: "borrador",
      elementos: elementos.length,
      criterios: totalCriterios,
      candidatosActivos: 0,
      certificadosEmitidos: 0,
      ultimaActualizacion: new Date().toISOString().slice(0, 10),
      tipo: meta.tipo || undefined,
      version: meta.version || undefined,
      periodoRevision: meta.periodoRevision || undefined,
      descripcion: meta.descripcion || undefined,
      proposito: meta.proposito || undefined,
      documentoFuente: doc
        ? { nombre: doc.nombre, tamanioKb: doc.tamanioKb, fechaCarga: new Date().toISOString().slice(0, 10) }
        : undefined,
    };
    addStandard(standard);
    if (elementos.length > 0) setElementsForStandard(meta.codigo, elementos);
    setRulesForStandard(meta.codigo, suggestRules({ codigo: meta.codigo }));
    // Persistimos los instrumentos que vinieron del análisis IA, vinculándolos a su
    // elemento y cubriendo todos los criterios de ese elemento (para que la cobertura
    // en criteria-config y los conteos en standard-detail muestren los valores reales).
    const instruments: Instrumento[] = [];
    elementos.forEach((e, ei) => {
      const parsedInst = e.instrumentos ?? [];
      if (parsedInst.length === 0) {
        instruments.push({
          id: `${meta.codigo}-inst-${ei + 1}`,
          nombre: `Lista de cotejo — ${e.codigo}`,
          tipo: "checklist",
          reactivos: suggestInstrumentItems(e).length,
          elementoId: e.id,
          criterioIds: e.criterios.map((c) => c.id),
        });
        return;
      }
      parsedInst.forEach((inst, ii) => {
        instruments.push({
          ...inst,
          id: `${meta.codigo}-inst-${ei + 1}-${ii + 1}`,
          elementoId: e.id,
          criterioIds: inst.criterioIds ?? e.criterios.map((c) => c.id),
        });
      });
    });
    if (instruments.length > 0) setInstrumentsForStandard(meta.codigo, instruments);
    // EC1781 es el estándar guiado de la demo: al darlo de alta poblamos candidatos,
    // grupos, expedientes, exámenes y ruta LMS para que todos los roles tengan datos.
    if (meta.codigo === "EC1781") {
      useDemoStore.getState().seedEc1781Bundle();
    }
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "crear", entidad: "standard", entidadId: meta.codigo });
    setLocation(`/admin/estandares/${meta.codigo}`);
  }

  return (
    <AppLayout breadcrumb={["Estándares", "Nuevo estándar"]}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>Alta de estándar de competencia</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
          Sube el documento oficial del estándar. La IA lo lee y propone todos los datos: tú solo revisas y confirmas.
        </div>
      </div>

      <Stepper step={step} />

      {error && (
        <div data-testid="error-banner" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>
      )}

      {step === 1 && (
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
          <Card>
            <SectionTitle>Paso 1 · Documento del estándar</SectionTitle>
            <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 14 }}>
              Sube el PDF oficial publicado por el CONOCER (o un .txt/.md con su contenido). La IA detectará código, nombre, tipo, versión, vigencia, sector, descripción, propósito, elementos, criterios, evidencias e instrumentos.
            </div>

            {!doc && (
              <div
                onClick={() => fileRef.current?.click()}
                style={{
                  border: "2px dashed var(--cc-gray-300)",
                  borderRadius: 12,
                  padding: 30,
                  textAlign: "center",
                  cursor: "pointer",
                  background: "var(--cc-gray-50, #fafbfc)",
                }}
                data-testid="dropzone"
              >
                <Upload size={28} color="var(--cc-navy)" />
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--cc-navy)", marginTop: 8 }}>Subir documento del estándar</div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginTop: 4 }}>PDF, TXT o MD · máximo 10 MB</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
                  style={{ display: "none" }}
                  data-testid="input-file"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }}
                />
              </div>
            )}

            {doc && (
              <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 10, padding: 14, display: "flex", alignItems: "center", gap: 12 }} data-testid="uploaded-doc">
                <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--cc-teal-bg)", display: "grid", placeItems: "center" }}>
                  <FileText size={20} color="var(--cc-teal)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-gray-800)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.nombre}</div>
                  <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{doc.tamanioKb.toLocaleString("es-MX")} KB · cargado correctamente</div>
                </div>
                <button
                  onClick={removeDoc}
                  data-testid="remove-doc"
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-gray-500)", padding: 4 }}
                  aria-label="Quitar documento"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", gap: 8 }}>
              <PrimaryButton variant="ghost" onClick={() => setLocation("/admin/estandares")} testId="button-cancel">Cancelar</PrimaryButton>
              <div style={{ display: "flex", gap: 8 }}>
                {doc && !parsing && (
                  <PrimaryButton variant="outline" onClick={() => runAI()} testId="button-rerun-ai">
                    <Sparkles size={13} /> {parsed ? "Reanalizar" : "Analizar con IA"}
                  </PrimaryButton>
                )}
                <PrimaryButton
                  variant="dark"
                  onClick={() => setStep(2)}
                  disabled={!parsed || parsing}
                  testId="button-to-meta"
                >
                  Revisar datos extraídos <ChevronRight size={14} />
                </PrimaryButton>
              </div>
            </div>
          </Card>

          <Card style={{ background: "linear-gradient(180deg, #fefce8 0%, #fffbeb 100%)", border: "1px solid #fcd34d" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Sparkles size={16} color="#d97706" />
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", letterSpacing: "0.04em", textTransform: "uppercase" }}>Lectura asistida por IA</div>
            </div>

            <AnimatePresence mode="wait">
              {!doc && !parsed && !parsing && (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ fontSize: 13, color: "#78350f", lineHeight: 1.55 }}>
                  Sube el documento del estándar y la IA lo analizará. Para EC1781 obtendrás el resultado canónico con sus 2 elementos y 17 criterios.
                </motion.div>
              )}

              {parsing && (
                <motion.div key="parsing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <ParsingAnimation />
                </motion.div>
              )}

              {parsed && !parsing && (
                <motion.div key="parsed" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ fontSize: 13, color: "#78350f", lineHeight: 1.55 }} data-testid="ia-result">
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontWeight: 700 }}>
                    <CheckCircle2 size={14} color="#16a34a" /> Análisis completado · confianza {(parsed.confianza * 100).toFixed(0)}%
                  </div>
                  <div style={{ display: "grid", gap: 4 }}>
                    <Row label="Código" value={parsed.codigo} />
                    <Row label="Tipo" value={parsed.tipo} />
                    <Row label="Versión" value={parsed.version} />
                    <Row label="Sector" value={parsed.sector} />
                    <Row label="Vigencia" value={parsed.vigencia} />
                  </div>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #fcd34d" }}>
                    <strong>{parsed.elementos.length}</strong> elementos · <strong>{parsed.elementos.reduce((a, e) => a + e.criterios.length, 0)}</strong> criterios
                    <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                      {parsed.elementos.map((e) => (
                        <li key={e.id}><strong>{e.codigo}</strong> — {e.titulo.slice(0, 60)}{e.titulo.length > 60 ? "…" : ""}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      )}

      {step === 2 && (
        <Card>
          <SectionTitle>Paso 2 · Revisar datos del estándar</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={14} color="#d97706" /> Datos pre-llenados por la IA. Edita lo que sea necesario antes de continuar.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Código" value={meta.codigo} onChange={(v) => setMeta({ ...meta, codigo: v.toUpperCase() })} testId="input-codigo" placeholder="EC1781" />
            <Field label="Tipo" value={meta.tipo} onChange={(v) => setMeta({ ...meta, tipo: v })} testId="input-tipo" placeholder="Estándar CONOCER" />
            <Field label="Nombre" full value={meta.titulo} onChange={(v) => setMeta({ ...meta, titulo: v })} testId="input-titulo" placeholder="Conducción de alto desempeño..." />
            <Field label="Versión" value={meta.version} onChange={(v) => setMeta({ ...meta, version: v })} testId="input-version" placeholder="Versión publicada / vigente" />
            <Field label="Vigencia del certificado" value={meta.vigencia} onChange={(v) => setMeta({ ...meta, vigencia: v })} testId="input-vigencia" placeholder="3 años" />
            <Field label="Periodo de revisión" value={meta.periodoRevision} onChange={(v) => setMeta({ ...meta, periodoRevision: v })} testId="input-periodo" placeholder="3 años" />
            <Field label="Sector" value={meta.sector} onChange={(v) => setMeta({ ...meta, sector: v })} testId="input-sector" placeholder="Seguridad pública" />
            <FieldEstado />
            <TextField label="Descripción" value={meta.descripcion} onChange={(v) => setMeta({ ...meta, descripcion: v })} testId="input-descripcion" />
            <TextField label="Propósito" value={meta.proposito} onChange={(v) => setMeta({ ...meta, proposito: v })} testId="input-proposito" />
          </div>

          {doc && (
            <div style={{ marginTop: 14, padding: 10, background: "var(--cc-gray-50)", border: "1px solid var(--cc-gray-200)", borderRadius: 8, display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
              <FileText size={14} color="var(--cc-teal)" />
              <span style={{ color: "var(--cc-gray-700)" }}>Documento fuente:</span>
              <strong style={{ color: "var(--cc-gray-900)" }}>{doc.nombre}</strong>
              <span style={{ color: "var(--cc-gray-500)" }}>· {doc.tamanioKb.toLocaleString("es-MX")} KB</span>
            </div>
          )}

          {codigoExists && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#b45309" }}>Ya existe un estándar con código <strong>{meta.codigo}</strong>.</div>
          )}

          <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between" }}>
            <PrimaryButton variant="outline" onClick={() => setStep(1)} testId="button-back-step2"><ChevronLeft size={14} /> Volver al documento</PrimaryButton>
            <PrimaryButton variant="dark" onClick={() => { if (validateMeta()) setStep(3); }} testId="button-to-structure">Continuar a estructura <ChevronRight size={14} /></PrimaryButton>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <SectionTitle>Paso 3 · Estructura sugerida y guardado</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            <Metric label="Elementos" value={parsed?.elementos.length ?? 0} />
            <Metric label="Criterios" value={parsed?.elementos.reduce((a, e) => a + e.criterios.length, 0) ?? 0} />
            <Metric label="Instrumentos" value={parsed?.elementos.reduce((a, e) => a + e.instrumentos.length, 0) ?? 0} />
          </div>

          {parsed && parsed.elementos.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {parsed.elementos.map((e) => (
                <div key={e.id} style={{ padding: 12, border: "1px solid var(--cc-gray-200)", borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Badge tone="navy">{e.codigo}</Badge>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-gray-800)" }}>{e.titulo}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>{e.criterios.length} criterios · {e.evidencias.length} evidencias · {e.instrumentos.length} instrumentos</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: 12, background: "var(--cc-teal-bg)", border: "1px solid var(--cc-teal)", borderRadius: 8, marginBottom: 14, fontSize: 12, color: "var(--cc-navy)" }}>
            Al guardar, el estándar queda en estado <strong>Borrador</strong> y aún no estará disponible para candidatos. Podrás editar elementos, criterios y publicarlo desde su detalle.
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <PrimaryButton variant="outline" onClick={() => setStep(2)} testId="button-back-step3"><ChevronLeft size={14} /> Volver</PrimaryButton>
            <PrimaryButton variant="dark" onClick={save} testId="button-save-standard"><Save size={14} /> Guardar como borrador</PrimaryButton>
          </div>
        </Card>
      )}
    </AppLayout>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const labels = ["Documento + IA", "Datos del estándar", "Estructura"];
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
      {labels.map((l, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const active = n === step;
        const done = n < step;
        return (
          <div key={l} data-testid={`step-${n}`} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: active ? "var(--cc-navy)" : done ? "var(--cc-teal-bg)" : "white", border: `1px solid ${active ? "var(--cc-navy)" : "var(--cc-gray-200)"}`, color: active ? "white" : done ? "var(--cc-teal)" : "var(--cc-gray-600)", fontSize: 12, fontWeight: 600 }}>
            Paso {n} · {l}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, value, onChange, full, testId, placeholder }: { label: string; value: string; onChange: (v: string) => void; full?: boolean; testId?: string; placeholder?: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: full ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        style={{ padding: "9px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, color: "var(--cc-gray-800)" }}
      />
    </label>
  );
}

function TextField({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: "1 / -1" }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={3}
        style={{ padding: "9px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, color: "var(--cc-gray-800)", fontFamily: "inherit", resize: "vertical" }}
      />
    </label>
  );
}

function FieldEstado() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Estado</span>
      <div style={{ padding: "9px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, color: "var(--cc-gray-700)", background: "var(--cc-gray-50, #fafbfc)" }}>
        <Badge tone="amber">Borrador</Badge>
        <span style={{ marginLeft: 8, fontSize: 12, color: "var(--cc-gray-500)" }}>(automático al crear)</span>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ padding: 12, background: "white", border: "1px solid var(--cc-gray-200)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
      <span style={{ color: "#92400e", fontWeight: 600 }}>{label}</span>
      <span style={{ color: "#78350f", textAlign: "right" }}>{value}</span>
    </div>
  );
}

function ParsingAnimation() {
  const steps = [
    "Leyendo documento...",
    "Detectando código y datos generales...",
    "Identificando elementos y criterios...",
    "Sugiriendo evidencias e instrumentos...",
  ];
  return (
    <div style={{ fontSize: 13, color: "#78350f" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
          <Cpu size={16} color="#d97706" />
        </motion.div>
        <strong>Analizando documento...</strong>
      </div>
      {steps.map((s, i) => (
        <motion.div
          key={s}
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.4 }}
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}
        >
          <span style={{ color: "#16a34a" }}>✓</span> {s}
        </motion.div>
      ))}
    </div>
  );
}
